import React, { useState } from "react";

// ── Design tokens ─────────────────────────────────────────────────────────────
const GOLD        = "#D4AF37";
const GOLD_DIM    = "rgba(212,175,55,.12)";
const GOLD_BORDER = "rgba(212,175,55,.28)";
const EMERALD     = "#064E3B";
const TEXT_MAIN   = "#1A202C";
const TEXT_MID    = "#4A5568";
const TEXT_SOFT   = "#718096";

const ELEM_COLOR = { 木:"#16A34A", 火:"#DC2626", 土:"#D4AF37", 金:"#78716C", 水:"#2563EB" };
const ELEM_EN    = { 木:"Wood", 火:"Fire", 土:"Earth", 金:"Metal", 水:"Water" };

// Palace verdicts (for element/xiyong sections)
const VERDICT_BADGE = {
  极佳:   { bg:"rgba(22,163,74,.12)",  color:"#15803D" },
  良好:   { bg:"rgba(22,163,74,.08)",  color:"#16A34A" },
  平稳:   { bg:"rgba(212,175,55,.10)", color:"#B8940A" },
  有压力: { bg:"rgba(234,88,12,.10)",  color:"#C2410C" },
  需化解: { bg:"rgba(220,38,38,.10)",  color:"#DC2626" },
};

// Activation badges
const ACTIVATION_BADGE = {
  强力激活: { bg:"rgba(22,163,74,.12)",  color:"#15803D" },
  平稳运作: { bg:"rgba(212,175,55,.10)", color:"#B8940A" },
  有所受阻: { bg:"rgba(220,38,38,.10)",  color:"#DC2626" },
};

// ── 四化 flow-type badges (Phase 48B) ─────────────────────────────────────────
const FLOW_BADGE = {
  流入:    { bg:"rgba(22,163,74,.12)",  color:"#15803D",  icon:"▸" },
  驱动:    { bg:"rgba(234,88,12,.10)",  color:"#C2410C",  icon:"⚡" },
  显化:    { bg:"rgba(37,99,235,.10)",  color:"#1D4ED8",  icon:"◎" },
  打结:    { bg:"rgba(220,38,38,.10)",  color:"#DC2626",  icon:"⊗" },
  "驱动 · 显化": { bg:"rgba(120,70,20,.10)", color:"#78450F", icon:"⚡◎" },
};

const BALANCE_COLOR = {
  过旺:"#DC2626", 偏强:"#C2410C", 均衡:"#16A34A", 偏弱:"#B8940A", 匮乏:"#DC2626",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function Badge({ label, bg, color }) {
  return (
    <span style={{
      display:"inline-block", padding:"2px 8px", borderRadius:99,
      background: bg || GOLD_DIM, color: color || GOLD,
      fontSize:11, fontWeight:600, letterSpacing:.4,
    }}>{label}</span>
  );
}

function FlowBadge({ flowLabel }) {
  const s = FLOW_BADGE[flowLabel] || { bg:GOLD_DIM, color:GOLD, icon:"·" };
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:3,
      padding:"2px 8px", borderRadius:99,
      background:s.bg, color:s.color,
      fontSize:11, fontWeight:700, letterSpacing:.5,
    }}>
      {s.icon} {flowLabel}
    </span>
  );
}

/** Extracts flow type from a sihua string like "贪狼化禄" → "流入" */
function sihuaToFlow(sihuaStr) {
  if (sihuaStr.includes("化禄")) return "流入";
  if (sihuaStr.includes("化权")) return "驱动";
  if (sihuaStr.includes("化科")) return "显化";
  if (sihuaStr.includes("化忌")) return "打结";
  return null;
}

function SectionHeader({ title, sub, open, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      width:"100%", textAlign:"left", background:"none", border:"none",
      cursor:"pointer", padding:"14px 18px",
      display:"flex", alignItems:"center", justifyContent:"space-between",
      borderBottom: open ? `1px solid ${GOLD_BORDER}` : "none",
    }}>
      <div>
        <div style={{ fontSize:13, fontWeight:700, color:TEXT_MAIN, letterSpacing:.3 }}>{title}</div>
        {sub && <div style={{ fontSize:11, color:TEXT_SOFT, marginTop:2 }}>{sub}</div>}
      </div>
      <span style={{ fontSize:13, color:GOLD, transform:open?"rotate(180deg)":"none", transition:"transform .2s" }}>▾</span>
    </button>
  );
}

// ── Dim 1: 元素宫位映射 ────────────────────────────────────────────────────────
function ElementPalaceMapping({ data }) {
  return (
    <div style={{ padding:"16px 18px", display:"flex", flexDirection:"column", gap:10 }}>
      {data.map(item => {
        const col = ELEM_COLOR[item.element] || GOLD;
        return (
          <div key={item.element} style={{
            display:"flex", gap:12, alignItems:"flex-start", padding:"10px 12px",
            borderRadius:8,
            background: item.is_daymaster ? "rgba(212,175,55,.07)" : "rgba(255,255,255,.6)",
            border:`1px solid ${item.is_daymaster ? GOLD_BORDER : "rgba(0,0,0,.06)"}`,
          }}>
            <div style={{
              flexShrink:0, width:36, height:36, borderRadius:8,
              background:col+"20", border:`1.5px solid ${col}40`,
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            }}>
              <span style={{ fontSize:15, fontWeight:700, color:col }}>{item.element}</span>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, fontWeight:600, color:TEXT_MAIN }}>
                  {ELEM_EN[item.element]}
                  {item.is_daymaster && <span style={{ color:GOLD, marginLeft:4 }}>· 日主</span>}
                </span>
                <span style={{ fontSize:11, color:BALANCE_COLOR[item.balance] || TEXT_MID }}>
                  {item.balance}
                </span>
                <span style={{ fontSize:11, color:TEXT_SOFT }}>({item.count}/8)</span>
              </div>
              <div style={{ fontSize:11, color:TEXT_SOFT, marginTop:2 }}>{item.balance_desc}</div>
              {item.key_palaces.length > 0 && (
                <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                  {item.key_palaces.map(p => {
                    const vb = VERDICT_BADGE[p.verdict] || VERDICT_BADGE["平稳"];
                    return (
                      <div key={p.palace} style={{
                        display:"flex", alignItems:"center", gap:4, padding:"3px 8px",
                        borderRadius:6, background:vb.bg, border:`1px solid ${vb.color}30`,
                      }}>
                        <span style={{ fontSize:11, fontWeight:600, color:vb.color }}>{p.palace}</span>
                        {p.stars.length > 0 && (
                          <span style={{ fontSize:10, color:TEXT_SOFT }}>· {p.stars.join("/")}</span>
                        )}
                        {/* Flow badges for sihua */}
                        {p.sihua.map(s => {
                          const fl = sihuaToFlow(s);
                          return fl ? <FlowBadge key={s} flowLabel={fl} /> : (
                            <span key={s} style={{ fontSize:10, color:GOLD }}>· {s}</span>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Dim 2: 用神星曜激活 ────────────────────────────────────────────────────────
function XiyongActivation({ data }) {
  const { yong_shen, xi_shen, summary_bias } = data;
  const renderCard = (item) => {
    const ab = ACTIVATION_BADGE[item.activation] || ACTIVATION_BADGE["平稳运作"];
    const col = ELEM_COLOR[item.element] || GOLD;
    return (
      <div style={{
        flex:1, padding:"12px 14px", borderRadius:8,
        background:"rgba(255,255,255,.7)", border:"1px solid rgba(0,0,0,.07)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
          <span style={{
            display:"inline-flex", width:26, height:26, borderRadius:6,
            background:col+"20", color:col, fontSize:13, fontWeight:700,
            alignItems:"center", justifyContent:"center",
          }}>{item.element}</span>
          <span style={{ fontSize:12, fontWeight:600, color:TEXT_MAIN }}>{item.role}</span>
          <span style={{ marginLeft:"auto" }}>
            <Badge label={item.activation} bg={ab.bg} color={ab.color} />
          </span>
        </div>
        <div style={{ fontSize:11, color:TEXT_SOFT, marginBottom:8 }}>{item.activation_desc}</div>
        {item.palaces.map(p => {
          const vb = VERDICT_BADGE[p.verdict] || VERDICT_BADGE["平稳"];
          return (
            <div key={p.palace} style={{
              display:"flex", alignItems:"center", gap:4, marginTop:4,
              padding:"4px 8px", borderRadius:5, background:vb.bg,
            }}>
              <span style={{ fontSize:11, fontWeight:600, color:vb.color }}>{p.palace}</span>
              <span style={{ fontSize:10, color:TEXT_SOFT }}>{p.palace_en}</span>
              {p.stars.length > 0 && (
                <span style={{ fontSize:10, color:TEXT_MID, marginLeft:4 }}>{p.stars.join("·")}</span>
              )}
              {p.auspicious_sihua.map(s => {
                const fl = sihuaToFlow(s);
                return fl ? <FlowBadge key={s} flowLabel={fl} /> : (
                  <span key={s} style={{ fontSize:10, color:GOLD, marginLeft:"auto" }}>{s}</span>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div style={{ padding:"16px 18px" }}>
      {summary_bias && (
        <div style={{ fontSize:11, color:GOLD, fontStyle:"italic", marginBottom:10, paddingLeft:2 }}>
          {summary_bias}
        </div>
      )}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        {renderCard(yong_shen)}
        {renderCard(xi_shen)}
      </div>
    </div>
  );
}

// ── Dim 3 & 4: 身体 / 事业融合 ────────────────────────────────────────────────
function FusionCard({ icon, title, verdict, stars, sihua, synthesis, focus, advice }) {
  const vb = VERDICT_BADGE[verdict] || VERDICT_BADGE["平稳"];
  return (
    <div style={{
      flex:1, padding:"14px 16px", borderRadius:8,
      background:"rgba(255,255,255,.7)", border:"1px solid rgba(0,0,0,.07)", minWidth:240,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
        <span style={{ fontSize:16 }}>{icon}</span>
        <span style={{ fontSize:12, fontWeight:700, color:TEXT_MAIN, whiteSpace:"nowrap" }}>{title}</span>
        <span style={{ marginLeft:"auto" }}>
          <Badge label={verdict} bg={vb.bg} color={vb.color} />
        </span>
      </div>
      {(stars.length > 0 || sihua.length > 0) && (
        <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:6 }}>
          {stars.map(s => (
            <span key={s} style={{
              fontSize:11, padding:"2px 6px", borderRadius:4, background:GOLD_DIM, color:GOLD, fontWeight:600,
            }}>{s}</span>
          ))}
          {sihua.map(s => {
            const fl = sihuaToFlow(s);
            return fl ? <FlowBadge key={s} flowLabel={fl} /> : (
              <span key={s} style={{
                fontSize:11, padding:"2px 6px", borderRadius:4,
                background:"rgba(22,163,74,.10)", color:"#15803D", fontWeight:600,
              }}>{s}</span>
            );
          })}
        </div>
      )}
      <div style={{ fontSize:11, color:TEXT_MID, marginBottom:6, lineHeight:1.6 }}>{synthesis}</div>
      {(focus || advice) && (
        <div style={{ fontSize:11, color:EMERALD, lineHeight:1.6, borderLeft:`2px solid ${GOLD}`, paddingLeft:8 }}>
          {focus || advice}
        </div>
      )}
    </div>
  );
}

// ── Dim 5: 时机融合 ───────────────────────────────────────────────────────────
function TimingFusion({ data }) {
  const {
    bazi_dayun, bazi_period_quality, bazi_xiyong_hits,
    ziwei_decade_palace, ziwei_decade_stars, ziwei_decade_years,
    timing_alignment, timing_advice,
  } = data;
  const isPositive = timing_alignment.includes("顺势") || timing_alignment.includes("共振");
  const isNegative = timing_alignment.includes("承压") || timing_alignment.includes("休养");
  const badgeBg    = isPositive ? "rgba(22,163,74,.12)" : isNegative ? "rgba(220,38,38,.10)" : GOLD_DIM;
  const badgeColor = isPositive ? "#15803D" : isNegative ? "#DC2626" : GOLD;
  return (
    <div style={{ padding:"16px 18px" }}>
      <div style={{
        display:"flex", alignItems:"center", gap:10, marginBottom:14,
        padding:"10px 14px", borderRadius:8,
        background:badgeBg, border:`1px solid ${badgeColor}30`,
      }}>
        <span style={{ fontSize:18 }}>{isPositive?"🌟":isNegative?"🛡":"⚖️"}</span>
        <span style={{ fontSize:13, fontWeight:700, color:badgeColor }}>{timing_alignment}</span>
      </div>
      <div style={{ fontSize:12, color:TEXT_MID, lineHeight:1.7, marginBottom:14 }}>{timing_advice}</div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        <div style={{
          flex:1, minWidth:180, padding:"10px 12px", borderRadius:7,
          background:"rgba(255,255,255,.65)", border:"1px solid rgba(0,0,0,.06)",
        }}>
          <div style={{ fontSize:10, letterSpacing:.5, color:TEXT_SOFT, marginBottom:4, textTransform:"uppercase" }}>
            八字大运
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:TEXT_MAIN }}>{bazi_dayun || "—"}</div>
          {bazi_period_quality && (
            <div style={{ fontSize:11, color:TEXT_MID, marginTop:4 }}>{bazi_period_quality}</div>
          )}
          {bazi_xiyong_hits.length > 0 && (
            <div style={{ marginTop:4, display:"flex", gap:4, flexWrap:"wrap" }}>
              {bazi_xiyong_hits.map(h => (
                <span key={h} style={{ fontSize:10, padding:"1px 5px", borderRadius:4,
                  background:"rgba(22,163,74,.10)", color:"#15803D" }}>{h}</span>
              ))}
            </div>
          )}
        </div>
        <div style={{
          flex:1, minWidth:180, padding:"10px 12px", borderRadius:7,
          background:"rgba(255,255,255,.65)", border:"1px solid rgba(0,0,0,.06)",
        }}>
          <div style={{ fontSize:10, letterSpacing:.5, color:TEXT_SOFT, marginBottom:4, textTransform:"uppercase" }}>
            紫微大限
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:TEXT_MAIN }}>
            {ziwei_decade_palace || "—"}
            {ziwei_decade_years.length === 2 && (
              <span style={{ fontSize:11, color:TEXT_SOFT, fontWeight:400, marginLeft:6 }}>
                {ziwei_decade_years[0]}–{ziwei_decade_years[1]}
              </span>
            )}
          </div>
          {ziwei_decade_stars.length > 0 && (
            <div style={{ marginTop:4, display:"flex", gap:4, flexWrap:"wrap" }}>
              {ziwei_decade_stars.map(s => (
                <span key={s} style={{ fontSize:10, padding:"1px 5px", borderRadius:4, background:GOLD_DIM, color:GOLD }}>{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dim 6: 四化能量流 (Phase 48B) ─────────────────────────────────────────────
function SihuaEnergyFlow({ data }) {
  const { cards } = data;
  if (!cards || cards.length === 0) return null;

  return (
    <div style={{ padding:"16px 18px", display:"flex", flexDirection:"column", gap:12 }}>
      {cards.map((card, ci) => {
        const fb = FLOW_BADGE[card.type] || FLOW_BADGE["流入"];
        const hasItems = card.items && card.items.length > 0;
        return (
          <div key={ci} style={{
            borderRadius:8, overflow:"hidden",
            border:`1px solid ${fb.color}25`,
            background:"rgba(255,255,255,.65)",
          }}>
            {/* Card header */}
            <div style={{
              padding:"10px 14px", borderBottom:`1px solid ${fb.color}18`,
              background:fb.bg,
              display:"flex", alignItems:"flex-start", gap:10,
            }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <FlowBadge flowLabel={card.type} />
                  <span style={{ fontSize:10, color:TEXT_SOFT }}>{card.type_en}</span>
                </div>
                {card.flow_desc && (
                  <div style={{ fontSize:11, color:TEXT_MID, lineHeight:1.6 }}>
                    {card.flow_desc}
                  </div>
                )}
              </div>
            </div>
            {/* Items */}
            {hasItems ? (
              <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:8 }}>
                {card.items.map((item, ii) => (
                  <div key={ii} style={{
                    padding:"8px 10px", borderRadius:6,
                    background:"rgba(255,255,255,.7)",
                    border:"1px solid rgba(0,0,0,.06)",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:item.effect ? 5 : 0 }}>
                      {/* Star badge */}
                      <span style={{
                        fontSize:11, padding:"1px 7px", borderRadius:4,
                        background:GOLD_DIM, color:GOLD, fontWeight:600,
                      }}>{item.star}</span>
                      <span style={{ fontSize:11, color:TEXT_SOFT }}>→</span>
                      {/* Palace */}
                      <span style={{ fontSize:12, fontWeight:700, color:TEXT_MAIN }}>{item.palace}</span>
                      {/* Palace stars */}
                      {item.palace_stars.length > 0 && (
                        <span style={{ fontSize:10, color:TEXT_SOFT }}>
                          [{item.palace_stars.join("·")}]
                        </span>
                      )}
                    </div>
                    {/* Effect narrative */}
                    {item.effect && (
                      <div style={{
                        fontSize:11, color:TEXT_MID, lineHeight:1.65,
                        borderLeft:`2px solid ${fb.color}60`, paddingLeft:8,
                        marginTop:3,
                      }}>
                        {item.effect}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding:"10px 14px", fontSize:11, color:TEXT_SOFT, fontStyle:"italic" }}>
                本命盘此类四化无落点
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function EnergyCrossAnalysis({ data, lang = "zh" }) {
  const [open, setOpen] = useState({
    mapping:true, xiyong:true, health:true, career:true, timing:true, sihua:true,
  });
  const toggle = k => setOpen(o => ({ ...o, [k]:!o[k] }));

  if (!data || data.error) return null;

  const {
    element_palace_mapping, xiyong_star_activation,
    health_fusion, career_fusion, timing_fusion, sihua_energy,
  } = data;

  const sectionStyle = {
    borderRadius:10, border:`1px solid ${GOLD_BORDER}`,
    background:"rgba(255,255,255,.55)", overflow:"hidden", marginBottom:10,
  };

  return (
    <div style={{ fontFamily:"'Inter','Montserrat',sans-serif" }}>
      {/* Header */}
      <div style={{
        display:"flex", alignItems:"center", gap:10, marginBottom:16,
        paddingBottom:12, borderBottom:`1px solid ${GOLD_BORDER}`,
      }}>
        <div style={{ width:3, height:22, background:GOLD, borderRadius:2, flexShrink:0 }}/>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:TEXT_MAIN, letterSpacing:.3 }}>
            能量交叉透视
          </div>
          <div style={{ fontSize:11, color:TEXT_SOFT, marginTop:1 }}>
            八字五行平衡 × 紫微星曜布局 · 6维深度融合
          </div>
        </div>
      </div>

      {/* 1. 元素宫位映射 */}
      <div style={sectionStyle}>
        <SectionHeader title="元素宫位映射" sub="五行在紫微十二宫的投影分布"
          open={open.mapping} onToggle={() => toggle("mapping")} />
        {open.mapping && <ElementPalaceMapping data={element_palace_mapping} />}
      </div>

      {/* 2. 用神星曜激活 */}
      <div style={sectionStyle}>
        <SectionHeader title="用神星曜激活" sub="喜用元素在紫微宫位的落星状态"
          open={open.xiyong} onToggle={() => toggle("xiyong")} />
        {open.xiyong && <XiyongActivation data={xiyong_star_activation} />}
      </div>

      {/* 3+4. 身体 & 事业（并排） */}
      <div style={{ marginBottom:10 }}>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ flex:1, borderRadius:10, border:`1px solid ${GOLD_BORDER}`, background:"rgba(255,255,255,.55)", overflow:"hidden", minWidth:240 }}>
            <SectionHeader title="身体融合" sub="八字弱元素 × 疾厄宫"
              open={open.health} onToggle={() => toggle("health")} />
            {open.health && (
              <div style={{ padding:"14px 16px" }}>
                <FusionCard icon="🫁"
                  title={`疾厄宫 · ${health_fusion.health_palace_verdict}`}
                  verdict={health_fusion.health_palace_verdict}
                  stars={health_fusion.health_palace_stars}
                  sihua={health_fusion.health_palace_sihua}
                  synthesis={health_fusion.synthesis}
                  focus={health_fusion.focus}
                />
              </div>
            )}
          </div>
          <div style={{ flex:1, borderRadius:10, border:`1px solid ${GOLD_BORDER}`, background:"rgba(255,255,255,.55)", overflow:"hidden", minWidth:240 }}>
            <SectionHeader title="事业融合" sub="八字事业信号 × 官禄宫"
              open={open.career} onToggle={() => toggle("career")} />
            {open.career && (
              <div style={{ padding:"14px 16px" }}>
                <FusionCard icon="🏛"
                  title={`官禄宫 · ${career_fusion.career_palace_verdict}`}
                  verdict={career_fusion.career_palace_verdict}
                  stars={career_fusion.career_palace_stars}
                  sihua={career_fusion.career_palace_sihua}
                  synthesis={career_fusion.synthesis}
                  advice={career_fusion.day_elem_advice}
                />
                {career_fusion.bazi_career_signal && (
                  <div style={{
                    marginTop:10, padding:"8px 10px", borderRadius:6,
                    background:"rgba(6,78,59,.05)", border:"1px solid rgba(6,78,59,.12)",
                    fontSize:11, color:"#065F46", lineHeight:1.6,
                  }}>
                    <span style={{ fontWeight:600 }}>八字信号：</span>{career_fusion.bazi_career_signal}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. 时机融合 */}
      <div style={sectionStyle}>
        <SectionHeader title="时机融合" sub="当前八字大运 × 紫微大限 协同判断"
          open={open.timing} onToggle={() => toggle("timing")} />
        {open.timing && <TimingFusion data={timing_fusion} />}
      </div>

      {/* 6. 四化能量流 (Phase 48B) */}
      {sihua_energy && (
        <div style={sectionStyle}>
          <SectionHeader title="四化能量流" sub="流入 · 驱动 · 显化 · 打结 — 能量运作的底层结构"
            open={open.sihua} onToggle={() => toggle("sihua")} />
          {open.sihua && <SihuaEnergyFlow data={sihua_energy} />}
        </div>
      )}
    </div>
  );
}
